// HR "More" tab — the six desktop nav items that don't get a bottom tab,
// plus sign-out. Below `md` the desktop `Shell` (and with it the sidebar's
// nav list and sign-out button) isn't rendered at all, so without this page a
// phone could not reach Members, Policies, Resources, Integrations, Settings
// or New case, and could not sign out.
//
// Each entry links out to its existing `/hr/*` page, which already renders
// acceptably at phone width inside the app shell — none needed a
// mobile-native rebuild (PO decision, 2026-07-30).
//
// Reachable at every width: the tab bar only appears below `md`, but the path
// stays valid above it (a resized window, a shared link) rather than 404ing.
// Padding is the shell's job, so this adds none of its own.

import {
  UsersIcon,
  UserIcon,
  DocIcon,
  BookIcon,
  PlugIcon,
  SlidersIcon,
} from '@/components/ui/icons.tsx';
import { NavCard } from '@/components/ui/Card.tsx';
import { Button } from '@/components/ui/Button.tsx';
import { useSession } from '@/lib/auth.ts';
import { ROLE_LABELS, useSignOut } from '@/components/Shell.tsx';

export function HrMore() {
  const session = useSession('hr-shell');
  const logout = useSignOut();
  const name = 'name' in session ? session.name : '';

  return (
    <div>
      <h1 className="mb-4 text-lg font-semibold">More</h1>
      <ul className="space-y-3">
        <li>
          <NavCard to="/hr/members" title="Members" icon={<UserIcon />} />
        </li>
        <li>
          <NavCard to="/hr/policies" title="Policies" icon={<DocIcon />} />
        </li>
        <li>
          <NavCard to="/hr/resources" title="Resources" icon={<BookIcon />} />
        </li>
        <li>
          <NavCard to="/hr/integrations" title="Integrations" icon={<PlugIcon />} />
        </li>
        <li>
          <NavCard to="/hr/settings" title="Settings" icon={<SlidersIcon />} />
        </li>
        {/* Desktop parity: "New support pathway" is HR_Admin-only
            (HrShell.tsx). Support pathways themselves are their own tab; this
            is a shortcut straight to the create form, mirroring the desktop
            nav's separate "New support pathway" item. */}
        {session.role === 'HR_Admin' ? (
          <li>
            <NavCard to="/hr/cases/new" title="New support pathway" icon={<UsersIcon />} />
          </li>
        ) : null}
      </ul>

      {/* Mirrors the desktop sidebar's footer — who you're signed in as, and
          the way out. Below `lg` the app shell has no other place for either;
          `lg:hidden` above that, since `AppSidebar` (hardcoded `lg:flex` in
          AppNav.tsx, independent of HrShell's `breakpoint="md"` prop on
          `Shell`) shows the same name/role/sign-out there. `md:hidden` would
          leave a 768–1023px gap where `MobileTopNav` has taken over from the
          tab bar but the sidebar hasn't appeared yet — it has no name/role,
          only a generic eyebrow + Sign out. */}
      <section className="mt-8 border-t border-border pt-4 lg:hidden">
        <h2 className="sr-only">Your account</h2>
        <p className="text-sm font-medium text-foreground">
          {name || ROLE_LABELS[session.role] || 'Signed in'}
        </p>
        <p className="text-xs text-muted-foreground">{ROLE_LABELS[session.role]}</p>
        {/* No Settings link here — the nav list above already has one, and a
            second would give the page two links with the same accessible
            name. */}
        <div className="mt-3">
          <Button
            variant="ghost"
            className="px-0 py-0 text-sm"
            onClick={() => logout.mutate()}
            loading={logout.isPending}
          >
            Sign out
          </Button>
        </div>
      </section>
    </div>
  );
}
